import { Controller } from "@hotwired/stimulus";
import RequestHelper from "../helpers/request_helper";

// The AI Gift Assistant form on the give checkout path.
//
// select2 and the Bootstrap tooltip are jQuery plugins, so their bindings live in
// connect()/disconnect() rather than data-actions - select2 announces through
// `.trigger("change")`, which dispatches no real DOM event.
//
// R4 skip: "hidden", "btn-disabled" and "has-error" are all app-wide utility classes with no
// local override in app/assets/stylesheets/lasso/{,v2/pages/}gives/ai.scss (D1) - "hidden" comes
// from bootstrap-yeti.scss, "btn-disabled" from unties/base.scss + marketing_2016/base.scss +
// lasso/buttons.scss, "has-error" from bootstrap-yeti.scss + lasso/v2/components/forms.scss.
// Moving any of them onto the Classes API would just re-declare the same literal with no
// decoupling, plus the P33 missing-attribute throw risk, for zero benefit.
export default class extends Controller {
  static get targets() {
    return [
      "generateButton", "domesticPricesContainer", "internationalPricesContainer", "generalError",
      "tooltip", "form", "sizeCalculator"
    ];
  }

  connect() {
    this.$promptLocation = $(this.formTarget).find(".prompt-location");
    this.$promptDomesticPrice = $(this.formTarget).find("#domestic-price");
    this.$promptInternationalPrice = $(this.formTarget).find("#international-price");
    this.$tooltip = $(this.tooltipTarget);

    this.onLocationChange = this.onLocationChange.bind(this);
    this.onDomesticPriceChange = this.onDomesticPriceChange.bind(this);
    this.onInternationalPriceChange = this.onInternationalPriceChange.bind(this);

    this.$promptLocation.select2({
      placeholder: "Select...",
      minimumResultsForSearch: Infinity
    });

    this.$promptDomesticPrice.select2({
      placeholder: "$ price",
      minimumResultsForSearch: Infinity
    });

    this.$promptInternationalPrice.select2({
      placeholder: "$ price",
      minimumResultsForSearch: Infinity
    });

    this.$promptDomesticPrice.prop("disabled", true);
    this.$promptInternationalPrice.prop("disabled", true);

    this.$promptLocation.on("change", this.onLocationChange);
    this.$promptDomesticPrice.on("change", this.onDomesticPriceChange);
    this.$promptInternationalPrice.on("change", this.onInternationalPriceChange);

    this.formTarget.querySelectorAll(".fancy-input input").forEach((input) => this.resizeInput(input));
  }

  disconnect() {
    this.$promptLocation.off("change", this.onLocationChange);
    this.$promptDomesticPrice.off("change", this.onDomesticPriceChange);
    this.$promptInternationalPrice.off("change", this.onInternationalPriceChange);

    // Symmetric with the select2() calls in connect() - without this, a Turbo cache restore that
    // re-runs connect() re-initializes select2 on top of its own leftover markup. Guarded the same
    // way as the rest of the codebase's select2-using controllers (e.g.
    // collection_generator_prompt_controller.js, gift_events_controller.js).
    if (this.$promptLocation.data("select2")) { this.$promptLocation.select2("destroy"); }
    if (this.$promptDomesticPrice.data("select2")) { this.$promptDomesticPrice.select2("destroy"); }
    if (this.$promptInternationalPrice.data("select2")) { this.$promptInternationalPrice.select2("destroy"); }
  }

  resizeInput(input) {
    const inputStyles = window.getComputedStyle(input);

    this.sizeCalculatorTarget.textContent = input.value || input.placeholder;
    this.sizeCalculatorTarget.style.fontSize = inputStyles.fontSize;
    this.sizeCalculatorTarget.style.fontFamily = inputStyles.fontFamily;
    this.sizeCalculatorTarget.style.fontWeight = inputStyles.fontWeight;
    this.sizeCalculatorTarget.style.letterSpacing = inputStyles.letterSpacing;

    input.style.width = this.sizeCalculatorTarget.offsetWidth + "px";
  }

  checkFormValidity() {
    if (this.formTarget.checkValidity()) {
      this.generateButtonTarget.classList.remove("btn-disabled");
      this.$tooltip.tooltip("disable");
    } else {
      this.$tooltip.tooltip("enable");
      this.generateButtonTarget.classList.add("btn-disabled");
    }
  }

  hideErrors(input) {
    input.classList.remove("has-error");
    this.generalErrorTarget.classList.add("hidden");
  }

  onPromptInput(event) {
    this.resizeInput(event.target);
    this.hideErrors(event.target);
  }

  onLocationChange() {
    const isInternational = this.$promptLocation.val() === "true";

    this.$promptDomesticPrice.val("").trigger("change");
    this.$promptInternationalPrice.val("").trigger("change");

    if (isInternational) {
      this.internationalPricesContainerTarget.classList.remove("hidden");
      this.domesticPricesContainerTarget.classList.add("hidden");
      this.$promptInternationalPrice.prop("disabled", false);
      this.$promptDomesticPrice.prop("disabled", true);
    } else {
      this.domesticPricesContainerTarget.classList.remove("hidden");
      this.internationalPricesContainerTarget.classList.add("hidden");
      this.$promptDomesticPrice.prop("disabled", false);
      this.$promptInternationalPrice.prop("disabled", true);
    }

    this.checkFormValidity();
  }

  onDomesticPriceChange() {
    this.hideErrors(this.$promptDomesticPrice[0]);
    this.checkFormValidity();
  }

  onInternationalPriceChange() {
    this.hideErrors(this.$promptInternationalPrice[0]);
    this.checkFormValidity();
  }

  submit(event) {
    event.preventDefault();
    this.generateButtonTarget.disabled = true;

    const params = {
      name: this.formTarget.querySelector("input#name")?.value,
      occasion: this.formTarget.querySelector("input#occasion")?.value,
      email: this.formTarget.querySelector("input#email")?.value,
      international: this.formTarget.querySelector("select#international")?.value,
      price: this.formTarget.querySelector("select.prompt-price:not(:disabled)")?.value
    };

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify(params);

    fetch(this.formTarget.getAttribute("action"), options)
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (ok) {
          // Our own controller's JSON response, same origin as this page - asserted before
          // navigating per the S3 rule rather than trusting `data.redirect` outright. The
          // failing branch does not navigate.
          if (RequestHelper.isSameOriginUrl(data.redirect)) {
            window.location.href = data.redirect;
            return;
          }

          this.generateButtonTarget.disabled = false;
          this.generalErrorTarget.textContent = "Something went wrong. Please try again.";
          this.generalErrorTarget.classList.remove("hidden");
          return;
        }

        this.generateButtonTarget.disabled = false;

        Object.keys(data.errors).forEach((key) => {
          if (key === "base") {
            this.generalErrorTarget.textContent = `${data.errors[key]}. Please try again.`;
            this.generalErrorTarget.classList.remove("hidden");
          } else {
            // R2 skip: `key` is a server error field name (name/occasion/email/international/
            // price), so this selects a different element on every call - there is no single
            // fixed target the Targets API could name here.
            this.element.querySelectorAll(`.prompt-${key}`).forEach((el) => el.classList.add("has-error"));
          }
        });
      });
  }
}
